import { alignChannelFromBackend, type BackendChannel, CHANNEL } from 'src/data/channel';
import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import { EMAIL_CSV_RESOURCE } from 'src/data/performance';
import type { Entity, ID } from 'src/lib/collection';

export interface CustomModel extends Entity {
  accountName: string;
  brandVoiceName: string;
  dataSource: BackendChannel;
}

export const CUSTOM_MODEL_STATUS = {
  created: 'created',
  training_in_progress: 'training_in_progress',
  recreating: 'recreating',
  error: 'error',
  active: 'active', // after progress
} as const;
export type CustomModelStatus = (typeof CUSTOM_MODEL_STATUS)[keyof typeof CUSTOM_MODEL_STATUS];

const PROCESSING_ERROR_TYPE = {
  notEnoughDatasetError: 'not_enough_dataset_error',
  notEnoughRelevantDataError: 'not_enough_relevant_data_error',
  assetTypeNotSupportedError: 'asset_type_not_supported_error',
  notEnoughEnglishDatasetError: 'not_enough_english_dataset_error',
  systemError: 'system_error',
} as const;
export type ProcessingErrorType =
  (typeof PROCESSING_ERROR_TYPE)[keyof typeof PROCESSING_ERROR_TYPE];

const PROCESSING_ERROR_TYPE_MESSAGE_MAP: Record<ProcessingErrorType, string> = {
  [PROCESSING_ERROR_TYPE.notEnoughDatasetError]: 'Not enough dataset examples to train the model',
  [PROCESSING_ERROR_TYPE.notEnoughRelevantDataError]:
    'Custom model training failed. Not enough training assets were available',
  [PROCESSING_ERROR_TYPE.assetTypeNotSupportedError]:
    'Custom model training failed. Not enough training assets were available',
  [PROCESSING_ERROR_TYPE.notEnoughEnglishDatasetError]:
    'Custom model training failed. Not enough training assets were available',
  [PROCESSING_ERROR_TYPE.systemError]: 'something went wrong',
};

export const getProcessingErrorTypeMessage = (type: ProcessingErrorType): string =>
  PROCESSING_ERROR_TYPE_MESSAGE_MAP?.[type] ||
  'We were unable to create a custom model. Please try again later.';

export const getCustomModelTexts = (customModelId: ID, customModels: CustomModel[]) => {
  const selectedCustomModel = customModels?.find((customModel) => customModel.id === customModelId);

  return {
    customModelTitle: selectedCustomModel?.accountName,
    customModelLabel: selectedCustomModel?.brandVoiceName,
  };
};

export const getGenerationToolFromCustomModel = (
  customModel: CustomModel
): GenerationTool | undefined => {
  const channel = alignChannelFromBackend(customModel?.dataSource);
  switch (channel) {
    case CHANNEL.facebook:
      return GENERATION_TOOL.facebookMixtureAd;

    case CHANNEL.linkedin:
      return GENERATION_TOOL.linkedinMixtureAd;

    case CHANNEL.linkedinSocial:
      return GENERATION_TOOL.linkedinPost;

    case CHANNEL.facebookPages:
      return GENERATION_TOOL.facebookPost;

    case CHANNEL.instagram:
      return GENERATION_TOOL.instagramCaption;

    case CHANNEL.hubspot:
    case CHANNEL.salesforce:
    case CHANNEL.marketo:
    case CHANNEL.braze:
    case CHANNEL.iterable:
    case CHANNEL.salesforceExactTarget:
    case EMAIL_CSV_RESOURCE.emailColdCsvResource:
    case EMAIL_CSV_RESOURCE.emailPromotionCsvResource:
      return GENERATION_TOOL.emailMixture;

    case EMAIL_CSV_RESOURCE.emailSubjectLineCsvResource:
      return GENERATION_TOOL.emailSubject;

    case CHANNEL.blogUrls:
      return GENERATION_TOOL.blogDescription;

    default:
      return undefined;
  }
};
